import Vue from 'vue';
import workPlaceService from '@/api/work-place-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { updateArrayItem, removeArrayItem } from '@/util/array';
import { mapErrorsToInputs } from '@/util/forms';

const getDefaultWorkPlaceFormItem = () => ({
  worked_abroad: false,
  until_now: false,
  is_visible: true,
});

export const getDefaultWorkPlaceFilterParams = () => ({});

const state = {
  workPlaces: null,
  workPlacePagination: {},
  editedWorkPlace: {},
  workPlaceValidationErrors: {},
  workPlaceFilterParams: getDefaultWorkPlaceFilterParams(),
  newWorkPlace: getDefaultWorkPlaceFormItem(),
};

const getters = {
  workPlaceTypes() {
    return [
      {
        name: i18n.t('work'),
        value: 'work',
      },
      {
        name: i18n.t('studies'),
        value: 'studies',
      },
      {
        name: i18n.t('period_of_unemployment'),
        value: 'period_of_unemployment',
      },
      {
        name: i18n.t('period_of_unemployment_not_official'),
        value: 'period_of_unemployment_not_official',
      },
    ];
  },
};

const mutations = {
  SET_WORK_PLACES(state, { data, current_page, per_page, total }) {
    state.workPlaces = data;
    state.workPlacePagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_WORK_PLACE_FILTER_PARAMS(state, params) {
    state.workPlaceFilterParams = params;
  },

  SET_EDITED_WORK_PLACE(state, workPlace) {
    state.workPlaceValidationErrors = {};
    state.editedWorkPlace = JSON.parse(JSON.stringify(workPlace));
  },

  STORE_WORK_PLACE(state, workPlace) {
    state.workPlaces = [...state.workPlaces, workPlace];
    state.workPlacePagination.total += 1;
    state.workPlaceValidationErrors = {};
    state.newWorkPlace = getDefaultWorkPlaceFormItem();
  },

  UPDATE_WORK_PLACE(state, workPlace) {
    state.workPlaces = updateArrayItem(state.workPlaces, workPlace);
  },

  DELETE_WORK_PLACE(state, workPlace) {
    state.workPlaces = removeArrayItem(state.workPlaces, workPlace);
    state.workPlacePagination.total -= 1;
  },

  SET_WORK_PLACE_VALIDATION_ERRORS(state, workPlaceValidationErrors) {
    state.workPlaceValidationErrors = workPlaceValidationErrors;
  },

  RESET_WORK_PLACE_VALIDATION_ERRORS(state) {
    state.workPlaceValidationErrors = {};
  },

  CLEAR_WORK_PLACE_VALIDATION_ERRORS(state, field) {
    Vue.delete(state.workPlaceValidationErrors, field);
  },
};

const actions = {
  fetchWorkPlaces({ commit }, params) {
    commit('SET_WORK_PLACE_FILTER_PARAMS', params);
    return workPlaceService.getPage(params).then((res) => {
      commit('SET_WORK_PLACES', res.data);
      if (params.page !== state.workPlaceFilterParams.page) {
        window.scrollTo(0, 0);
      }
      return res.data;
    });
  },

  storeWorkPlace({ commit }, workPlace) {
    return workPlaceService
      .create(workPlace)
      .then((res) => {
        commit('STORE_WORK_PLACE', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('work_place_created'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_WORK_PLACE_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editWorkPlace({ state, commit }, workPlaceId) {
    const workPlace = state.workPlaces?.find((c) => c.id === workPlaceId);
    if (workPlace) {
      commit('SET_EDITED_WORK_PLACE', workPlace);
      return Promise.resolve(workPlace);
    }
    return workPlaceService.getById(workPlaceId).then((res) => {
      commit('SET_EDITED_WORK_PLACE', res.data);
      return res.data;
    });
  },

  updateWorkPlace({ commit }, workPlace) {
    return workPlaceService
      .update(workPlace)
      .then((res) => {
        commit('UPDATE_WORK_PLACE', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('work_place_updated'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_WORK_PLACE_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  clearWorkPlaceValidationErrors({ commit }, field) {
    commit('CLEAR_WORK_PLACE_VALIDATION_ERRORS', field);
  },

  deleteWorkPlace({ commit }, workPlace) {
    openConfirmDialog({
      title: i18n.t('confirm_work_place_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      workPlaceService.delete(workPlace).then(() => {
        commit('DELETE_WORK_PLACE', workPlace);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('work_place_deleted'));
      });
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
